(function () {
  'use strict';

  if (window.customElements.get('faq-blog-search')) return;

  const indexes = new Map();
  const normalize = (text) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  async function fetchIndex(blogUrl, sectionId) {
    const blog = new URL(blogUrl, window.location.origin);
    let url = new URL(blog);
    const entries = [];
    const visited = new Set();

    while (url) {
      url.searchParams.set('section_id', sectionId);
      if (url.origin !== blog.origin || url.pathname !== blog.pathname || visited.has(url.href)) {
        throw new Error('Invalid FAQ index pagination.');
      }
      visited.add(url.href);

      const response = await fetch(url.href, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('FAQ index request failed.');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const index = document.querySelector('[data-faq-search-index]');
      if (!index) throw new Error('FAQ index was not returned.');

      index.querySelectorAll('[data-faq-search-entry]').forEach((template) => {
        entries.push({
          title: normalize(template.dataset.title || ''),
          text: normalize(template.dataset.searchText || ''),
          template
        });
      });
      url = index.dataset.nextUrl ? new URL(index.dataset.nextUrl, url) : null;
    }
    return entries;
  }

  function loadIndex(blogUrl, sectionId) {
    const key = blogUrl + ':' + sectionId;
    if (!indexes.has(key)) {
      const promise = fetchIndex(blogUrl, sectionId).catch((error) => {
        indexes.delete(key);
        throw error;
      });
      indexes.set(key, promise);
    }
    return indexes.get(key);
  }

  class FaqBlogSearch extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.input = this.querySelector('[data-faq-search-input]');
      this.form = this.querySelector('[data-faq-search-form]');
      this.clearButton = this.querySelector('[data-faq-search-clear]');
      this.results = this.querySelector('[data-faq-search-results]');
      this.list = this.querySelector('[data-faq-search-list]');
      this.message = this.querySelector('[data-faq-search-message]');
      this.status = this.querySelector('[data-faq-search-status]');
      if (!this.input || !this.form || !this.clearButton || !this.results || !this.list || !this.message || !this.status) return;

      this.version = 0;
      this.activeIndex = -1;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.input.addEventListener('input', () => this.onInput(), options);
      this.input.addEventListener('keydown', (event) => this.onKeydown(event), options);
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        window.clearTimeout(this.timer);
        this.search();
      }, options);
      this.clearButton.addEventListener('click', () => {
        this.input.value = '';
        this.onInput();
        this.input.focus();
      }, options);
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.close();
      }, options);
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.close();
      }, options);
    }

    disconnectedCallback() {
      this.close();
      if (this.events) this.events.abort();
      this.events = null;
    }

    getTerm() {
      return this.input.value.trim().replace(/\s+/g, ' ');
    }

    onInput() {
      this.close();
      this.list.replaceChildren();
      this.status.textContent = '';
      const term = this.getTerm();
      this.clearButton.hidden = !term;
      if (term.length < 2) return;
      this.timer = window.setTimeout(() => this.search(), 250);
    }

    open() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.version = (this.version || 0) + 1;
      window.clearTimeout(this.timer);
      if (!this.results) return;
      this.results.hidden = true;
      this.results.removeAttribute('aria-busy');
      this.input.setAttribute('aria-expanded', 'false');
      this.select(-1);
    }

    showMessage(text) {
      this.list.replaceChildren();
      this.message.textContent = text;
      this.message.hidden = false;
      this.status.textContent = text;
      this.select(-1);
      this.open();
    }

    async search() {
      const term = this.getTerm();
      if (term.length < 2) {
        this.close();
        this.status.textContent = 'Enter at least two characters.';
        return;
      }
      const version = ++this.version;
      this.showMessage('Searching questions...');
      this.results.setAttribute('aria-busy', 'true');

      try {
        const entries = await loadIndex(this.dataset.blogUrl, this.dataset.sectionId);
        if (version !== this.version || term !== this.getTerm() || !this.isConnected) return;
        const query = normalize(term);
        const words = query.split(' ');
        const matches = entries.filter((entry) => words.every((word) => entry.text.includes(word)));
        // Prefer complete question matches, then questions matching every word in their title.
        const score = (entry) => entry.title.includes(query) ? 2 : words.every((word) => entry.title.includes(word)) ? 1 : 0;
        matches.sort((a, b) => score(b) - score(a));
        this.results.removeAttribute('aria-busy');

        if (!matches.length) {
          this.showMessage('No matching questions found. Try fewer words, a product name or a SKU.');
          return;
        }
        this.message.hidden = true;
        this.list.replaceChildren();
        matches.slice(0, 10).forEach((entry, index) => {
          const item = entry.template.content.firstElementChild.cloneNode(true);
          item.id = this.list.id + '-' + index;
          // Links remain clickable and reachable through Tab as well as arrow keys.
          item.querySelector('a').removeAttribute('tabindex');
          this.list.append(item);
        });
        this.status.textContent = matches.length + ' matching answers. Showing ' + Math.min(matches.length, 10) + '.';
        this.open();
      } catch (error) {
        if (version !== this.version || !this.isConnected) return;
        this.results.removeAttribute('aria-busy');
        this.showMessage('We could not load suggestions. Please try again.');
      }
    }

    select(index) {
      this.activeIndex = index;
      if (!this.list) return;
      Array.from(this.list.children).forEach((item, itemIndex) => {
        item.setAttribute('aria-selected', String(itemIndex === index));
      });
      const item = this.list.children[index];
      if (item) {
        this.input.setAttribute('aria-activedescendant', item.id);
        item.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      const count = this.list.children.length;
      if (this.results.hidden || !count) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.select(this.activeIndex < 0 ? (step > 0 ? 0 : count - 1) : (this.activeIndex + step + count) % count);
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        this.list.children[this.activeIndex].querySelector('a').click();
      }
    }
  }

  window.customElements.define('faq-blog-search', FaqBlogSearch);
})();
